import { useDispatch, useSelector } from 'react-redux';
import { selectTransactions, selectTransactionsStatus } from 'store/transactions/selectors';
import { useEffect } from 'react';
import { getTransactions } from 'store/transactions/actions';
import qs from 'qs';
import { pick } from 'utils/pickObjectProperty';
import { QUERY_PARAMS } from 'shared/consts';
import { replace } from 'store/router/actions';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { useLocation } from 'react-router';
import { isRejectedStatus } from 'components/booking-tile/rejection.util';

export const useRedirectUserToLanding = (): void => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const queryParams = useSearchParams();
  const isResourcePage = pathname.includes('resource-booking/resource/');
  const isFirstPage = window.history.state.idx === 0;
  const hasSkipRedirectParam = queryParams.skipRedirect === 'true';
  const shouldRedirectIfTransactionsExist = isResourcePage && isFirstPage;
  const getTransactionsStatus = useSelector(selectTransactionsStatus);
  const transactions = useSelector(selectTransactions);

  useEffect(() => {
    if (shouldRedirectIfTransactionsExist && !getTransactionsStatus) {
      dispatch(getTransactions.request({}));
    }
  }, [shouldRedirectIfTransactionsExist, getTransactionsStatus, dispatch]);

  // RES-1174. Counts only bookings the tenant still has ahead of them. This redirect asks "does
  // this tenant already have something booked, so should a cold deep link to a resource page send
  // them to their bookings instead" -- and a rejected booking is not something booked. Since
  // RES-1174 the shared transactions list carries rejections, so without this a tenant whose only
  // upcoming row is a rejection would be bounced off a resource link that used to work for them.
  // `Array.isArray` rather than `transactions?.some(...)`: the previous check was `?.length`,
  // which tolerates a non-array, and the Router renders this hook on every route -- a throw here
  // takes the whole app down rather than skipping a redirect.
  const hasLiveTransactions =
    Array.isArray(transactions) && transactions.some(transaction => !isRejectedStatus(transaction?.details?.status));

  useEffect(() => {
    if (shouldRedirectIfTransactionsExist && hasLiveTransactions && !hasSkipRedirectParam) {
      const queryString = qs.stringify(
        pick(
          queryParams,
          QUERY_PARAMS.filter(
            param =>
              param !== 'startTime' &&
              param !== 'startDate' &&
              param !== 'endTime' &&
              param !== 'isExact' &&
              param !== 'duration' &&
              param !== 'skipRedirect',
          ),
        ),
      );
      dispatch(replace(`/?${queryString}`));
    }
  }, [shouldRedirectIfTransactionsExist, transactions, dispatch, queryParams, hasSkipRedirectParam]);
};
